import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useRemovePaymentMutation,
  useAttachContributionToPaymentMutation,
  useEditStudentWithAgreementMutation,
  ContributionInput,
  FakturowniaInvoicesDocument,
  StudentDetailDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

interface IStudentLike {
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    middleName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
}

interface IAgreementLike {
  id: string;
  agreementTitle: string;
  currency?: string | null;
  budgetAmount?: number | null;
  group: { id: string };
  date?: string | null;
  comment?: string | null;
  paymentByCompany?: boolean | null;
  vat?: boolean | null;
  resident?: boolean | null;
  terminated?: boolean | null;
  terminatedDate?: string | null;
  partialAttendance?: boolean | null;
  promocode?: string | null;
  country?: { id: string } | null;
  company?: { id: string } | null;
  contributions: {
    edges: Array<{
      node?: { id: string; date: string; amountForeign?: number | null } | null;
    } | null>;
  };
}

interface IAddContributionArgs {
  student: IStudentLike;
  agreement: IAgreementLike;
  date: string;
  amountForeign: number;
}

interface IPaymentMutationsHook {
  deletePayment: (paymentId: string) => Promise<boolean>;
  linkContribution: (
    contributionId: string,
    paymentId: string,
  ) => Promise<boolean>;
  /**
   * Adds a single Contribution to the agreement's schedule. Used by the
   * inline «+ Взнос в план» action on PaymentsBlock when the agreement
   * has no PandaDoc-driven schedule (or the operator needs an extra
   * planned row). Internally uses `editStudentWithAgreement` with the
   * full existing schedule + the new row appended — the mutation does
   * not support a single-row patch (see backend §10.1 candidate).
   */
  addContribution: (args: IAddContributionArgs) => Promise<boolean>;
  loading: boolean;
}

/**
 * Payment/contribution mutations behind one hook for the student card.
 * ТЗ 0004: Payment is a hidden technical record — the manual
 * `createPayment` / `updatePayment` / `unlinkContribution` surfaces were
 * removed from the UI; only `deletePayment` (reset a wrongly recorded
 * fact), `linkContribution` (attach a legacy orphan Payment to its взнос)
 * and `addContribution` remain. Each call refetches `StudentDetail` (so
 * the card's KPI strip and AgreementCard progress bar repaint) and
 * `fakturowniaInvoices` (so the orphan-invoices block reflects the
 * fresh unlinked/linked state).
 *
 * Mirrors the toast/error vocabulary of `useInvoiceMatching` — same
 * `GlobalToastTypeNames` channel, same `customErrors` pattern.
 */
export const usePaymentMutations = (
  studentId?: string,
): IPaymentMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const refetchQueries = useMemo(() => {
    const queries: Array<
      | { query: typeof StudentDetailDocument; variables?: { id: string } }
      | { query: typeof FakturowniaInvoicesDocument }
    > = [{ query: FakturowniaInvoicesDocument }];
    if (studentId) {
      queries.push({
        query: StudentDetailDocument,
        variables: { id: studentId },
      });
    }
    return queries;
  }, [studentId]);

  const [deleteMutation, deleteState] = useRemovePaymentMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [attachMutation, attachState] = useAttachContributionToPaymentMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [
    editAgreementMutation,
    editAgreementState,
  ] = useEditStudentWithAgreementMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const notifySuccess = useCallback(
    (title: string) => {
      addToast({
        data: { type: ToastTypes.success, title },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  const notifyError = useCallback(
    (
      title: string,
      errors?: Array<{
        message?: string | null;
        code?: string | null;
      } | null> | null,
    ) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title,
          text:
            errors
              ?.map((e) => e?.message || e?.code || null)
              .filter(Boolean)
              .join('\n') || undefined,
        },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  const deletePayment = useCallback(
    async (paymentId: string) => {
      try {
        const result = await deleteMutation({
          variables: { id: paymentId },
        });
        const payload = result.data?.deletePayment;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.payments.toasts.deleted'));
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.deleteFailed'),
          payload?.customErrors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.studentDetail.payments.toasts.deleteFailed'), [
          { message: (error as Error).message, code: null },
        ]);
        return false;
      }
    },
    [deleteMutation, notifySuccess, notifyError, t],
  );

  const linkContribution = useCallback(
    async (contributionId: string, paymentId: string) => {
      try {
        const result = await attachMutation({
          variables: { input: { contributionId, paymentId } },
        });
        const payload = result.data?.linkContributionToPayment;
        if (payload?.success) {
          notifySuccess(
            t('pages.studentDetail.payments.toasts.contributionLinked'),
          );
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.contributionLinkFailed'),
          payload?.customErrors,
        );
        return false;
      } catch (error) {
        notifyError(
          t('pages.studentDetail.payments.toasts.contributionLinkFailed'),
          [{ message: (error as Error).message, code: null }],
        );
        return false;
      }
    },
    [attachMutation, notifySuccess, notifyError, t],
  );

  const addContribution = useCallback(
    async ({
      student,
      agreement,
      date,
      amountForeign,
    }: IAddContributionArgs) => {
      try {
        const existing: ContributionInput[] = (
          agreement.contributions?.edges || []
        )
          .map((edge) => edge?.node)
          .filter((node): node is NonNullable<typeof node> => Boolean(node))
          .map((node) => ({
            date: node.date,
            sum: Number(node.amountForeign ?? 0),
          }));
        const nextContributions: ContributionInput[] = [
          ...existing,
          { date, sum: amountForeign },
        ];
        const u = student.user;
        const result = await editAgreementMutation({
          variables: {
            agreementId: agreement.id,
            updateStudentInput: {
              student: {
                firstName: u?.firstName || '',
                lastName: u?.lastName || '',
                middleName: u?.middleName || '',
                email: u?.email || '',
                phone: u?.phone || '',
              },
              agreement: {
                agreementTitle: agreement.agreementTitle,
                currency: agreement.currency || '',
                group_id: agreement.group.id,
                paymentByCompany: agreement.paymentByCompany ?? false,
                partialAttendance: agreement.partialAttendance ?? false,
                startAgreementBudget:
                  typeof agreement.budgetAmount === 'number'
                    ? agreement.budgetAmount
                    : null,
                countryId: agreement.country?.id || null,
                promocode: agreement.promocode || null,
                vat: agreement.vat ?? false,
                resident: agreement.resident ?? true,
                terminated: agreement.terminated ?? false,
                date: agreement.date || null,
                comment: agreement.comment || null,
                terminatedDate: agreement.terminatedDate || null,
              },
              company: agreement.company?.id
                ? { companyId: agreement.company.id }
                : null,
              contributions: nextContributions,
            },
          },
        });
        const payload = result.data?.updateStudentWithAgreement;
        if (payload?.success) {
          notifySuccess(
            t('pages.studentDetail.payments.toasts.contributionAdded'),
          );
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.contributionAddFailed'),
          payload?.customErrors,
        );
        return false;
      } catch (error) {
        notifyError(
          t('pages.studentDetail.payments.toasts.contributionAddFailed'),
          [{ message: (error as Error).message, code: null }],
        );
        return false;
      }
    },
    [editAgreementMutation, notifySuccess, notifyError, t],
  );

  return {
    deletePayment,
    linkContribution,
    addContribution,
    loading:
      deleteState.loading || attachState.loading || editAgreementState.loading,
  };
};
